export interface PushMessage {
  action:
    | 'hangup'
    | 'subscription'
    | 'billing'
    | 'call'
    | 'call_message'
    | 'call_status'
    | 'scheduled_call_status'
    | 'recording'
    | 'copilot'
    | 'media_start'
    | 'media_stop'
    | 'credits'
    | 'not_enough_credits'
    | 'number_verification'
    | 'number_verification_error'
    | 'contacts'
    | 'calendar'
    | 'wakeup'
    | (string & {})
  callSid?: string
  /** call_status carries the callSid as `id`; call_message uses `id` for the message */
  id?: string
  status?: string
  recording?: string
  text?: string
  sent?: boolean
  hash?: string
  question?: string
  number?: string
  credits?: number
  [key: string]: unknown
}

type Listener = (message: PushMessage) => void

/**
 * The listener registry lives on globalThis so that every module instance
 * shares it — under Vite HMR this file can be instantiated more than once,
 * and a stale FCM onMessage callback must still reach current listeners.
 */
const registry = globalThis as unknown as {
  __voypPushListeners?: Set<Listener>
  __voypPushChannel?: BroadcastChannel
}

const listeners: Set<Listener> = (registry.__voypPushListeners ??= new Set())

/** Subscribe to push messages (FCM foreground + service-worker relay). */
export function onPushMessage(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function emitPushMessage(message: PushMessage) {
  if (import.meta.env.DEV) {
    console.debug(`[push] ${message.action} → ${listeners.size} listener(s)`, message)
  }
  for (const listener of listeners) {
    try {
      listener(message)
    } catch (error) {
      console.error('push listener failed', error)
    }
  }
}

// Background messages relayed by firebase-messaging-sw.js. One channel only,
// no matter how many times HMR re-evaluates this module.
if (!registry.__voypPushChannel) {
  const channel = new BroadcastChannel('sw-messages')
  channel.addEventListener('message', (event) => {
    if (event.data?.action) emitPushMessage(event.data as PushMessage)
  })
  registry.__voypPushChannel = channel
}
